import { useEffect, useMemo, useState } from 'react';

import { GridSortModel } from '@mui/x-data-grid';
import useBlockedPlayers from 'api/useBlockedPlayers';
import { SortingDirection } from 'constants/enums';
import { Dayjs } from 'dayjs';
import { useDebounce } from 'hooks/useDebounce';
import { BlockedPlayer, EBlockedPlayerStatus, SortingBlockedPlayersValue } from 'views/Players/Players.types';

import { DEBOUNCE_MS, PAGE_SIZE } from './consts';
import { blockedPlayersUtils } from './utils';

export const useBlockedPlayersTable = () => {
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [searchByPlayerIdValue, setSearchByPlayerIdValue] = useState('');
  const [reasonFilterValue, setReasonFilterValue] = useState('');
  const [statusFilterValues, setStatusFilterValues] = useState<string[]>([]);
  const [rangeDates, setRangeDates] = useState<[Dayjs, Dayjs]>();
  const debouncedPlayerId = useDebounce(searchByPlayerIdValue, DEBOUNCE_MS);
  const debouncedReason = useDebounce(reasonFilterValue, DEBOUNCE_MS);
  const [sorting, setSorting] = useState<{
    sortValue: SortingBlockedPlayersValue;
    direction: SortingDirection;
  }>({
    sortValue: SortingBlockedPlayersValue.STATUS,
    direction: SortingDirection.ASC,
  });

  useEffect(() => {
    setPage(0);
  }, [debouncedPlayerId, debouncedReason, statusFilterValues, rangeDates]);

  const isBlocked: boolean | undefined = useMemo(() => {
    if (statusFilterValues.length === 1) {
      return statusFilterValues[0] === EBlockedPlayerStatus.BLOCKED;
    }
    return undefined;
  }, [statusFilterValues]);

  const { getBlockedPlayers: blockedPlayers } = useBlockedPlayers({
    limit: pageSize,
    offset: page * pageSize,
    playerId: debouncedPlayerId || undefined,
    banReason: debouncedReason || undefined,
    isBlocked,
    startDate: rangeDates?.[0].toISOString(),
    endDate: rangeDates?.[1].toISOString(),
  });

  const rows = useMemo(
    () =>
      (blockedPlayers.data?.blockedPlayers ?? []).map((playerData: BlockedPlayer) => ({
        _id: playerData.playerId,
        ...playerData,
      })),
    [blockedPlayers.data?.blockedPlayers]
  );

  const filters = blockedPlayersUtils.getBlockedPlayersFilters(
    setRangeDates,
    statusFilterValues,
    setStatusFilterValues,
    setReasonFilterValue,
    reasonFilterValue,
    rangeDates
  );

  const handleClearFilters = () => {
    setSearchByPlayerIdValue('');
    setReasonFilterValue('');
    setStatusFilterValues([]);
    setRangeDates(undefined);
    setPage(0);
  };

  const areFiltersApplied = !!(
    rangeDates ||
    debouncedPlayerId ||
    statusFilterValues.length > 0 ||
    debouncedReason
  );

  const isLoading = blockedPlayers.isLoading || (!blockedPlayers.isFetched && !blockedPlayers.isError);
  const isNoDataAvailable = rows.length === 0 && !isLoading && !areFiltersApplied;

  const handleRowsPerPageChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setPage(0);
  };

  const handleSortModelChange = (model: GridSortModel) => {
    if (model.length > 0) {
      setSorting({
        sortValue: model[0].field as SortingBlockedPlayersValue,
        direction: model[0].sort === SortingDirection.ASC ? SortingDirection.ASC : SortingDirection.DESC,
      });
    }
  };

  return {
    page,
    setPage,
    pageSize,
    rows,
    filters,
    sorting,
    isLoading,
    isError: blockedPlayers.isError,
    isNoDataAvailable,
    totalCount: blockedPlayers.data?.totalCount ?? 0,
    searchByPlayerIdValue,
    setSearchByPlayerIdValue,
    handleClearFilters,
    handleSortModelChange,
    handleRowsPerPageChange,
  };
};
